import * as pluralize from "pluralize";

import {
  featureFlags,
  Kaggle,
  LegacyDataSourceReference
} from "@kaggle/sdk";
import { getLogger, getServices } from "../../../api/getServices";
import {
  clearEditorError,
  editorErrorOccurred,
  setEditorStatus,
} from "../../redux/actions/editor";
import { setExternalSourceUrl } from "../../redux/actions/externalSourceUrl";
import { showCopyAndEditNudgeModal, showOverFailureThresholdSizeModal, showSaveDraftFailureModal } from "../../redux/actions/modal";
import {
  setSaveStatusToError,
  setSaveStatusToSaved,
} from "../../redux/actions/notebook";
import {
  appendTerminal,
  appendTerminalError,
} from "../../redux/actions/terminal";
import { getKernelsMaxSize } from "../../redux/selectors/acceleratorQuotaStats";
import {
  getCurrentAccelerator,
  getCurrentDataSourceReferences,
  getCurrentEditorStatus,
  getCurrentInternetEnabled,
  getCurrentIpynb,
  getCurrentKernelId,
  getCurrentLanguageId,
  getCurrentSequence,
} from "../../redux/selectors/current";
import { getParentKernelVersionId } from "../../redux/selectors/kernelDraft";
import { getSourceType } from "../../redux/selectors/session";
import { getStore } from "../../redux/store/appStore";
import { editorQueue } from "../../redux/utils/editorSnackbarQueue";
import { JupyterSource } from "../../session/JupyterSource";
import { Accelerator } from "../../types/Accelerator";
import { EditorStatus } from "../../types/EditorStatus";
import { LanguageId } from "../../types/language";
import { SourceType } from "../../types/SourceType";
import TerminalError from "../../types/TerminalError";
import TerminalMessage from "../../types/TerminalMessage";

const DISCONNECT_FAILURE_THRESHOLD = 5;
const RETRY_INTERVAL_MS = 5000;
const MAX_RETRY_COUNT = 120;
const MEBIBYTE = 1024 * 1024;

let showNotificationWhenStrippingOutput = true;

export interface SaveDraftRequest {
  kernelId: number;
  parentKernelVersionId: number | null;
  sequence: number;
  dataSources: LegacyDataSourceReference[];
  languageId: LanguageId;
  accelerator: Accelerator;
  isInternetEnabled: boolean;
  source: string | null;
  sourceType: SourceType;
  externalSourceUrl: string | null;
}

/** Response shape returned when a draft has been successfully saved. */
interface SuccessResponse {
  result: {
    computedParent?: Kaggle.Sdk.Kernels.Models.ParentKernelVersionData;
    sequence: number;
  };
  wasSuccessful: boolean;
}

/** Response shape returned when the server was unable to save a draft. */
interface ErrorResponse {
  errors: string[];
  wasSuccessful: boolean;
}

export type SaveDraftResponse = SuccessResponse | ErrorResponse;

/** Returns true if "Save Draft" response is successful. */
export function wasSuccessful(
  response: SaveDraftResponse
): response is SuccessResponse {
  return response.wasSuccessful;
}

/**
 * Returns a function that saves the latest app state as notebook draft.
 */
export const makeSaveDraft = () => {
  const { adhocClient } = getServices();
  let failureCount = 0;
  let lastErrorMessage = "";
  let retryQueued = false;
  // Variable toggling the display of the warning modal when the source is too large.
  let sizeExceededFailureCount = 0;

  // Variable toggling the display of the warning modal when there is a concurrency or connection/server error.
  // The modal is only displayed when the save has failed exactly five times, when failing more than five times a banner is displayed.
  const showWarningModal = () =>
    failureCount === DISCONNECT_FAILURE_THRESHOLD;
    
  const overFailureThreshold = () =>
    failureCount >= DISCONNECT_FAILURE_THRESHOLD;

  const warnOfDataLoss = (event: BeforeUnloadEvent) => {
    getLogger().reportImmediate('kernels', 'editor-warn-of-data-loss-shown');
    const message =
      'Changes you made may not be saved. To make a local copy, click "File" ' +
      'on the menu bar then "Download notebook".';
    event.returnValue = message;
    return message;
  };

  return () => {
    const { getState } = getStore();
    try {
      let state = getStore().getState();
      let sequence = getCurrentSequence(state);
      const kernelId = getCurrentKernelId(state);
      const languageId = getCurrentLanguageId(state);
      const status = getCurrentEditorStatus(state);

      if (kernelId === null) {
        throw new Error("Unable to save draft with null kernel id.");
      }

      if (sequence === null) {
        throw new Error("Unable to save draft with null sequence.");
      }

      if (languageId === null) {
        throw new Error("Unable to save draft with null language id.");
      }

      const saveStatus = status !== EditorStatus.QUEUED;

      if (!saveStatus) {
        throw new Error(
          `Unable to save draft while editor is in status: ${status}`
        );
      }

      return new Promise(async (res, rej) => {
        // Fetch the ipynb inside the Promise while the draftSequenceMutex is held.
        const ipynb = getCurrentIpynb(state);
        if (!ipynb) {
          rej(new Error(`Attempted to call notebook.saveDraft with invalid ipynb: ${ipynb}`));
          return;
        }

        if (overFailureThreshold()) {
          // Ignore requests while in retry loop.
          rej(new Error(`overFailureThreshold with error: ${lastErrorMessage.length > 0 ? lastErrorMessage : "No error"}`));
          return;
        }

        const handleErrors = (errors: string[]) => {
          const errorMessage = errors.join(". ");
          const isEditorRunning =
            getState().editor.status === EditorStatus.RUNNING;
          const isConcurrencyViolation = errors[0] === "ConcurrencyViolation";
          const isConnectionError = errors[0] === "Failed to fetch";
          const isServerError = !isConnectionError && !isConcurrencyViolation;

          failureCount++;
          if (errors.length > 0) {
              lastErrorMessage = errors[0];
          } else {
              lastErrorMessage = "No errors in handleErrors";
          }

          // If the failture count is >= 5, bad things are happening
          if (failureCount >= 5 && failureCount % 5 === 0) {
            getLogger().report("kernels", "save_draft_failure_count", {
              "kernelId": kernelId,
              "failureCount": failureCount,
              "timestamp": Date.now()
            });
          }

          // Retried saving source 120 times. Warn user and give up.
          if (failureCount > MAX_RETRY_COUNT) {
            editorErrorOccurred(
              "We were unable to save your notebook. To make a local copy, click " +
                '"File" on the menu bar then "Download notebook".'
            );
            rej(new Error(errorMessage));
            return;
          }
          // Failed to save source 5 consecutive times with concurrency error. Warn user and give up.
          if (
            overFailureThreshold() &&
            isEditorRunning &&
            isConcurrencyViolation
          ) {
            editorErrorOccurred(
              "This notebook has been modified in a different session. Please " +
                "refresh the page to load the latest changes."
            );
            setEditorStatus(EditorStatus.FATAL);
            rej(new Error(errorMessage));
            return;
          } else if (showWarningModal() && isEditorRunning &&
            (isServerError || isConnectionError)) {
              showSaveDraftFailureModal(isServerError);
          } else {
            // Only warn of data loss on unload for non concurrency violation errors.
            window.addEventListener("beforeunload", warnOfDataLoss);
          }

          // Stop accepting new save requests to avoid races and retry every 5s for up to 120 attempts.
          if (overFailureThreshold() && !retryQueued) {
            getLogger().report("kernels", "save_draft_loop", {
              "kernelId": kernelId,
              "location": "retryLooping",
              "timestamp": Date.now()
            });
            setTimeout(async () => {
              retryQueued = false;
              await saveDraft();
            }, RETRY_INTERVAL_MS);
            retryQueued = true;
          }

          // Log errors.
          appendTerminal([
            new TerminalError(errorMessage),
            new TerminalMessage(
              "The above error occurred while auto-saving draft."
            ),
          ]);
          setSaveStatusToError();
          rej(new Error(errorMessage));
        };

        const saveDraft = async () => {
          state = getState();
          sequence = getCurrentSequence(state);

          if (sequence == null) {
            // Editor now in an uninitialized state. Stop attempting saves.
            res();
            return;
          }          

          const maxSizeInMb = getKernelsMaxSize(state);
          let source = featureFlags.kernelsSaveCellOutput ? 
              new JupyterSource(ipynb).serialize() : new JupyterSource(ipynb).stripAndSerialize();
          if (featureFlags.kernelsSaveCellOutput && source.length > maxSizeInMb * MEBIBYTE) {
            source = new JupyterSource(ipynb).stripAndSerialize();
            // show the notification if the resize got it below the limit, 
            // otherwise we'll show the error below
            if (source.length <= maxSizeInMb * MEBIBYTE && showNotificationWhenStrippingOutput) {
              showNotificationWhenStrippingOutput = false;
              editorQueue.notify({
                title: `Upload size exceeds ${maxSizeInMb} MB. Cell outputs were removed.`,
                timeout: 2000,
                leading: true,
              });
            }
          } else {
            showNotificationWhenStrippingOutput = true;
          }
          if (source.length > maxSizeInMb * MEBIBYTE) {
            sizeExceededFailureCount++;
            if (sizeExceededFailureCount === 5) {
              showOverFailureThresholdSizeModal();
            }  
            appendTerminal([
              new TerminalError(
                `The kernel source must be less than ${maxSizeInMb} ${pluralize("megabytes", maxSizeInMb)} in size.`
              ),
              new TerminalMessage(
                "The above error occurred while auto-saving draft."
              ),
            ]);
            setSaveStatusToError();
            res();
            return;
          }

          try {
            const response = await adhocClient.saveDraft({
              kernelId,
              parentKernelVersionId: getParentKernelVersionId(state),
              sequence,
              dataSources: getCurrentDataSourceReferences(state),
              languageId,
              accelerator: getCurrentAccelerator(state),
              isInternetEnabled: getCurrentInternetEnabled(state),
              source,
              sourceType: getSourceType(state),
              externalSourceUrl: state.externalSourceUrl,
            });

            if (wasSuccessful(response)) {
              if (
                overFailureThreshold() &&
                (getState().editor.status === EditorStatus.FATAL ||
                  getState().editor.status === EditorStatus.STOPPED)
              ) {
                setEditorStatus(EditorStatus.RUNNING);
              }
              setExternalSourceUrl(null);
              failureCount = 0;
              sizeExceededFailureCount = 0;
              lastErrorMessage = "";
              window.removeEventListener("beforeunload", warnOfDataLoss);
              setSaveStatusToSaved(response.result.sequence);
              if (response.result.computedParent?.copyAndEditUrl) {
                showCopyAndEditNudgeModal(response.result.computedParent);
              }
              clearEditorError();
            } else {
              handleErrors(response.errors);
            }
          } catch (error) {
            handleErrors([error.message as string]);
          } finally {
            res();
          }
        };

        await saveDraft();
      });
    } catch (error) {
      appendTerminalError("An error occurred saving the draft.", error);
      throw error;
    }
  };
};
